/** The Run Kinematics report tab */
import { FunctionalMovementKinematicsGraph } from '@/pages/ReportV2/components/Graphs';
import {
  IonCol,
  IonGrid,
  IonRow,
  IonText,
  IonCardContent,
  IonCardTitle,
  IonIcon,
  IonImg,
  IonThumbnail,
  IonFabButton,
} from '@ionic/react';
import { colorPalette, helpCircleOutline, toggle } from 'ionicons/icons';
import { useEffect, useMemo, useState } from 'react';

import { BulletList } from '@/components/BulletList/BulletList';
import { KinematicsCardHeader } from '@/components/CardHeader/KinematicsCardHeader';
import { Card } from '@/components/Cards';
import { DataTable } from '@/components/DataTable/DataTable';
import { DataTableColumn } from '@/components/DataTable/DataTableColumn';
import { DataTableHeader } from '@/components/DataTable/DataTableHeader';
import { DataTableRow } from '@/components/DataTable/DataTableRow';
import { DataTableSection } from '@/components/DataTable/DataTableSection';
import { FloatingButton } from '@/components/FloatingButton/FloatingButton';
import { Icon } from '@/components/Icon';
import { IconSize } from '@/components/Icon/types';
import { InfoToast } from '@/components/Toasts/InfoToast';
import { LegendToast } from '@/components/Toasts/LegendToast';
import { Tooltip } from '@/components/Tooltip/Tooltip';

import SingleLegSquatAvatar from '@/assets/avatars/squat/singleLegSquatAvatar.png';
import HipCoronalMovement from '@/assets/icons/kinematicsIcons/hipCoronalMovement.png';
import HipTransversalMovement from '@/assets/icons/kinematicsIcons/hipTransversalMovement.png';
import KneeCoronalMovement from '@/assets/icons/kinematicsIcons/kneeCoronalMovement.png';

import { SingleLegSquatKinematicData } from '@/models/reportV2.model';

import styles from '@/pages/ReportV2/Styles/Reports.module.css';

type KinematicsReportProps = {
  tabData: SingleLegSquatKinematicData;
};

/** Run kinematics report tab
 * @param report - the full run report info and data
 */
export const SingleLegSquatsKinematicsReportTab = ({ tabData }: KinematicsReportProps) => {
  const [showAllStrides, setShowAllStrides] = useState<boolean>(false);
  const [showInfoToast, setShowInfoToast] = useState(false);
  const [showToast, setShowToast] = useState(false);

  const toggleShowAllStrides = () => {
    setShowAllStrides((prev) => {
      const newValue = !prev;
      localStorage.setItem('showAllStridesKinematics', newValue ? 'true' : 'false');
      return newValue;
    });
  };

  const seriesPelvis = useMemo(() => {
    return showAllStrides ? tabData.pelvisKinematicData.allStrides : tabData.pelvisKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesHip = useMemo(() => {
    return showAllStrides ? tabData.hipKinematicData.allStrides : tabData.hipKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesKnee = useMemo(() => {
    return showAllStrides ? tabData.kneeKinematicData.allStrides : tabData.kneeKinematicData.mean;
  }, [showAllStrides, tabData]);
  const seriesAnkle = useMemo(() => {
    return showAllStrides ? tabData.ankleKinematicData.allStrides : tabData.ankleKinematicData.mean;
  }, [showAllStrides, tabData]);

  const colors = ['tableLabel_succes', 'tableLabel_warning', 'tableLabel_danger', 'tableLabel_succes'];
  function getColor(index: number) {
    return colors[index % colors.length];
  }

  const handleShowToast = () => {
    setShowToast(true);
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  useEffect(() => {
    setShowInfoToast(true);
    return () => {
      setShowInfoToast(false);
    };
  }, []);

  if (!tabData) {
    return <IonText>Data not found.</IonText>;
  }
  return (
    <>
      <IonRow className='ion-no-padding'>
        <IonCol className='' size='12'>
          <Card style={{ height: 'fit-content', marginLeft: 0, marginRight: 0, marginTop: 0 }}>
            <KinematicsCardHeader />
            <IonCardContent>
              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px', backgroundColor: 'transparent' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Pelvis</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesPelvis.Sagittal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Anterior tilt (-) / Posterior tilt (+)'}
                        title={'Sagittal'}
                        yAxis={seriesPelvis.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesPelvis.Coronal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Drop (-) / Hike (+)'}
                        title={'Frontal'}
                        yAxis={seriesPelvis.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesPelvis.Transversal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Backward Rotation (-) / Forward Rotation (+)'}
                        title={'Transversal'}
                        yAxis={seriesPelvis.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Hip</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesHip.Sagittal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Sagittal'}
                        yAxis={seriesHip.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesHip.Coronal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Abduction (-) / Adduction (+)'}
                        title={'Frontal'}
                        yAxis={seriesHip.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesHip.Transversal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Transversal'}
                        yAxis={seriesHip.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow style={{ borderBottom: '1px solid #ddd', marginBottom: '6px' }}>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Knee</IonCardTitle>
                  </IonRow>
                  <IonRow className='ion-no-padding'>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesKnee.Sagittal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Extension (-) / Flexion (+)'}
                        title={'Sagittal'}
                        yAxis={seriesKnee.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesKnee.Coronal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Valgus (-) / Varus (+)'}
                        title={'Frontal'}
                        yAxis={seriesKnee.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesKnee.Transversal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Transversal'}
                        yAxis={seriesKnee.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>

              <IonRow>
                <IonGrid className='ion-no-padding'>
                  <IonRow className=''>
                    <IonCardTitle className={styles.title}>Ankle</IonCardTitle>
                  </IonRow>
                  <IonRow>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesAnkle.Sagittal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Plantarflexion (-) / Dorsalflexion (+)'}
                        title={'Sagittal'}
                        yAxis={seriesAnkle.Sagittal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesAnkle.Coronal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'Eversion (-) / Inversion (+)'}
                        title={'Frontal'}
                        yAxis={seriesAnkle.Coronal.yAxis}
                      />
                    </IonCol>
                    <IonCol className='ion-no-padding' size='4'>
                      <FunctionalMovementKinematicsGraph
                        data={seriesAnkle.Transversal.series}
                        strokeWidth={showAllStrides ? 1 : 2}
                        subtitle={'External Rotation (-) / Internal Rotation (+)'}
                        title={'Transversal'}
                        yAxis={seriesAnkle.Transversal.yAxis}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonRow>
            </IonCardContent>
          </Card>
        </IonCol>
      </IonRow>

      {/* Tables */}
      <IonRow className='ion-padding-bottom'>
        <Card style={{ margin: 0 }}>
          <IonCardContent>
            <IonRow className='ion-align-items-center ion-justify-content-center'>
              <IonCol className='' size='5'>
                <IonImg className={styles.squatsAvatar} src={SingleLegSquatAvatar} />
              </IonCol>
              <IonCol className='ion-no-padding' size='7'>
                <DataTable>
                  <DataTableHeader titles={['', 'Left', 'Right', 'Symmetry']} />

                  <DataTableSection>
                    <DataTableRow>
                      <DataTableColumn type='key'>Pelvis</DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[0][0].toFixed(1) + '°'} ± {tabData.romData[0][1].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[0][2].toFixed(1) + '°'} ± {tabData.romData[0][3].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>{tabData.romData[0][4].toFixed(0) + '%'}</DataTableColumn>
                    </DataTableRow>
                  </DataTableSection>
                  <DataTableSection>
                    <DataTableRow>
                      <DataTableColumn type='key'>Hip</DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[1][0].toFixed(1) + '°'} ± {tabData.romData[1][1].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[1][2].toFixed(1) + '°'} ± {tabData.romData[1][3].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>{tabData.romData[1][4].toFixed(0) + '%'}</DataTableColumn>
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        External rotation <IonIcon id='trigger-hip-external-rotation' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[2][0])} type='value'>
                        {tabData.hipLabels.externalRotationLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[2][1])} type='value'>
                        {tabData.hipLabels.externalRotationLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Internal rotation <IonIcon id='trigger-hip-internal-rotation' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[3][0])} type='value'>
                        {tabData.hipLabels.internalRotationLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[3][1])} type='value'>
                        {tabData.hipLabels.internalRotaionLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Abduction <IonIcon id='trigger-hip-abduction' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[4][0])} type='value'>
                        {tabData.hipLabels.abductionLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[4][1])} type='value'>
                        {tabData.hipLabels.abductionLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Adduction <IonIcon id='trigger-hip-adduction' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[5][0])} type='value'>
                        {tabData.hipLabels.adductionLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[5][1])} type='value'>
                        {tabData.hipLabels.adductionLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                  </DataTableSection>

                  <DataTableSection>
                    <DataTableRow>
                      <DataTableColumn type='key'>Knee</DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[2][0].toFixed(1) + '°'} ± {tabData.romData[2][1].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[2][2].toFixed(1) + '°'} ± {tabData.romData[2][3].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>{tabData.romData[2][4].toFixed(0) + '%'}</DataTableColumn>
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Varus / Valgus <IonIcon id='trigger-knee-valgus' src={helpCircleOutline} />
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[6][0])} type='value'>
                        {tabData.varusValgusLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[6][1])} type='value'>
                        {tabData.varusValgusLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                  </DataTableSection>

                  <DataTableSection>
                    <DataTableRow>
                      <DataTableColumn type='key'>Ankle</DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[3][0].toFixed(1) + '°'} ± {tabData.romData[3][1].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>
                        {tabData.romData[3][2].toFixed(1) + '°'} ± {tabData.romData[3][3].toFixed(1) + '°'}
                      </DataTableColumn>
                      <DataTableColumn type='value'>{tabData.romData[3][4].toFixed(0) + '%'}</DataTableColumn>
                    </DataTableRow>
                    <DataTableRow>
                      <DataTableColumn indent type='key'>
                        Early heel raise
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[0][0])} type='value'>
                        {tabData.ankleLabels.earlyHeelRaiseLabel_Left}
                      </DataTableColumn>
                      <DataTableColumn label={getColor(tabData.qualityData[0][1])} type='value'>
                        {tabData.ankleLabels.earlyHeelRaiseLabel_Right}
                      </DataTableColumn>
                      <DataTableColumn />
                    </DataTableRow>
                  </DataTableSection>
                </DataTable>
              </IonCol>
            </IonRow>
          </IonCardContent>
        </Card>
      </IonRow>

      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        side='bottom'
        trigger='trigger-hip-external-rotation'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={HipTransversalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Rotation <0°: Hip endorotates',
                  'Fair: Rotation between 10° & 20°',
                  'Poor: Rotation more than 20°',
                  'Rotation <10°: Rotation is less than 10°',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        side='bottom'
        trigger='trigger-hip-abduction'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={HipCoronalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Abduction <10°: Abduction less than 10°',
                  'Fair: Abduction between 10° & 20°',
                  'Poor: Abduction more than 20°',
                  'Abduction <0°: Adduction detected°',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        side='bottom'
        trigger='trigger-hip-adduction'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={HipCoronalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Adduction <10°: Adduction less than 10°',
                  'Fair: Adduction between 10° & 20°',
                  'Poor: Adduction more than 20°',
                  'Adduction >0°: Abduction detected°',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        side='bottom'
        trigger='trigger-hip-internal-rotation'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={HipTransversalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Rotation <-10°: Rotation is less than 10°',
                  'Fair: Rotation between -10° & -20°',
                  'Poor: Rotation more than -20°',
                  'Rotation >0°: Hip exorotates°',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>
      <Tooltip
        alignment='center'
        dismissOnSelect={true}
        reference='trigger'
        side='bottom'
        trigger='trigger-knee-valgus'
        triggerAction='click'>
        <IonGrid>
          <IonRow className='ion-align-items-center'>
            <IonCol className='ion-padding-end' size='auto'>
              <IonThumbnail>
                <IonImg src={KneeCoronalMovement} />
              </IonThumbnail>
            </IonCol>
            <IonCol>
              <BulletList
                listItems={[
                  'Valgus <10°',
                  'Fair: Valgus between 10° and 20°',
                  'Poor: Valgus more than 20°',
                  'Varus: Knee move outward',
                ]}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </Tooltip>

      <FloatingButton
        buttonIcon='sliders'
        horizontal='end'
        iconSize={IconSize.large}
        side='top'
        slot='fixed'
        vertical='bottom'>
        <IonFabButton onClick={() => toggleShowAllStrides()} title={'Toggle Mean / All Movements'}>
          <IonIcon icon={toggle}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={handleShowToast} title={'Show Graph Legend'}>
          <IonIcon icon={colorPalette}></IonIcon>
        </IonFabButton>
        <IonFabButton onClick={() => setShowInfoToast(true)} title={'Show Info'}>
          <Icon name={'info'}></Icon>
        </IonFabButton>
      </FloatingButton>

      <LegendToast
        duration={3000} // 3 seconds
        isOpen={showToast}
        onClose={handleCloseToast}
      />

      <InfoToast
        isOpen={showInfoToast}
        message='All graphs can be enlarged by clicking / tapping on the graph.'
        onClose={() => setShowInfoToast(false)}
      />
    </>
  );
};
